import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from '@themesberg/react-bootstrap'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Wrapper } from './CancelSubscriptionVerification.styled'
import SafeFormatMessage from '../../components/custom/Shared/SafeFormatMessage/SafeFormatMessage'
import useRequest from '../../axios/apis/useRequest'
import { MdError } from 'react-icons/md'
import { BsMailbox } from 'react-icons/bs'
import { FaEnvelope } from 'react-icons/fa'
import { setLoading } from '../../store/slices/main'
import { useNavigate } from 'react-router-dom'

const CancelSubscriptionVerification = () => {
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [orderID, setOrderID] = useState('')
  const [orderData, setOrderData] = useState()
  const [alertVisible, setAlertVisible] = useState(true)
  const [timer, setTimer] = useState(240)
  const dispatch = useDispatch()
  const recaptchaRef = useRef(null)
  const hash = window.location.hash
  const orderIDHash = hash
    .split('#')
    .find((element) => element.includes('order-'))

  const { getOrderByIdPublic, SubscriptionCancelationSubmit } = useRequest()

  useEffect(() => {
    if (orderIDHash) {
      setOrderID(orderIDHash.replace('order-', ''))
    }
  }, [orderIDHash])

  useEffect(() => {
    if (!orderData || !Object.values(orderData).length > 0) {
      dispatch(setLoading(true))
    }
    if (!orderID || (orderData && Object.values(orderData).length > 0)) return
    ;(async () => {
      const order = await getOrderByIdPublic(orderID)
      setOrderData(order.data.data)
    })()
    dispatch(setLoading(false))
  }, [orderID])

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [timer])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`
  }

  const direction = useSelector((state) => state.main.direction)

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .length(6, 'Your code should be 6 digits long')
      .required('Please enter the code you received'),
    recaptcha: Yup.string().required('Please complete the reCAPTCHA'),
  })
  const navigate = useNavigate()
  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      await SubscriptionCancelationSubmit({
        otpCode: values.code,
        recaptchaToken,
        subscriptionId: orderData.orderItems[0].subscriptionId,
      })
      navigate('/subscription-canceled-successfully')
    } catch (error) {
      setErrors({ submit: error.message })
    }
    setSubmitting(false)
  }

  return (
    <Wrapper>
      <section
        className="d-flex align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <Container className="d-flex justify-content-center">
          <Card
            className="cardCont shadow-soft border border-round border-light p-4 p-lg-5 w-100 fmxw-700"
            style={{
              height: '60vh',
              backgroundColor: 'var(--themeBackground)',
            }}
          >
            {alertVisible && (
              <Alert
                variant="danger"
                className="m-3"
                dismissible
                onClose={() => setAlertVisible(false)}
              >
                <MdError className="mx-2" />
                <SafeFormatMessage
                  id={'Are-you-sure-you-want-to-cancel-this-subscription?'}
                />
              </Alert>
            )}

            <Card.Body className="d-flex justify-content-center w-100">
              <Row>
                <Col
                  md={5}
                  className="d-flex align-items-center justify-content-center border-right-1 border-light"
                >
                  <Card.Title className="mb-0">
                    <span
                      className="fw-bold"
                      style={{ color: 'var(--primary4)' }}
                    >
                      <SafeFormatMessage id="Cancel-Subscription" />
                    </span>
                    <div
                      style={{
                        color: 'var(--primary4)',
                        fontSize: 'var(--normalFont)',
                        fontWeight: 'normal',
                      }}
                      className="mt-3"
                    >
                      {orderData?.orderItems[0]?.description}
                    </div>
                    <div className="fw-bold mb-3">
                      {orderData?.orderItems[0]?.displayName}
                    </div>
                  </Card.Title>
                </Col>
                <Col
                  md={7}
                  className={`my-2 d-flex align-items-center justify-content-start ${direction === 'ltr' ? 'pl-5' : 'pr-5'}`}
                >
                  <Formik
                    initialValues={{ code: '', recaptcha: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ handleSubmit, isSubmitting, setFieldValue }) => (
                      <Form onSubmit={handleSubmit} style={{ margin: 'auto' }}>
                        <Form.Group controlId="code" className="mb-2 w-100">
                          <Form.Label>
                            <SafeFormatMessage
                              id="Please-enter-code-to-verify"
                              defaultMessage={
                                'Enter the code we sent you to proceed'
                              }
                            />
                          </Form.Label>
                          <Field
                            className="form-control"
                            type="text"
                            name="code"
                            placeholder="Enter your code"
                            maxLength={6}
                          />
                          <ErrorMessage
                            name="code"
                            component="div"
                            className="text-danger small"
                          />
                        </Form.Group>

                        <div className="mb-2 text-muted small">
                          <SafeFormatMessage id="Time-remaining" />:{' '}
                          <span className="fw-bold">{formatTime(timer)}</span>
                        </div>

                        <Form.Group controlId="recaptcha" className="mb-3">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                            onChange={(val) => {
                              setRecaptchaToken(val)
                              setFieldValue('recaptcha', val)
                            }}
                          />
                          <ErrorMessage
                            name="recaptcha"
                            component="div"
                            className="text-danger small"
                          />
                        </Form.Group>

                        <Card.Footer className="px-0 mb-0 h-100 d-flex justify-content-center">
                          <Button
                            type="submit"
                            variant="danger"
                            style={{
                              width: '100%',
                              padding: '10px',
                              fontSize: '16px',
                            }}
                            disabled={isSubmitting || timer === 0}
                          >
                            <SafeFormatMessage
                              id="Cancel-Subscription"
                              defaultMessage={'Cancel Subscription'}
                            />
                          </Button>
                        </Card.Footer>
                      </Form>
                    )}
                  </Formik>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </section>
    </Wrapper>
  )
}

export default CancelSubscriptionVerification
